import { queryOptions } from '@tanstack/react-query';

import { cleanParams } from '@/utils/cleanParams';

import { apiDealFetch } from '..';
import type {
  DemandEntryDetail,
  DemandFilterOptionsRes,
  DemandListRes,
  DemandSummaryParams,
  DemandSummaryRes,
  GetDemandEntriesParams,
} from './types';

export const DEMAND_QUERY_KEYS = {
  demandEntries: 'demand-entries',
  demandEntry: 'demand-entry',
  demandSummary: 'demand-summary',
  demandFilterOptions: 'demand-filterOptions',
} as const;

export const toDemandListParams = ({
  companyIds,
  interestedPartyIds,
  introducerIds,
  ...rest
}: GetDemandEntriesParams) =>
  cleanParams({
    ...rest,
    companyIds: companyIds?.length ? companyIds.join(',') : undefined,
    interestedPartyIds: interestedPartyIds?.length ? interestedPartyIds.join(',') : undefined,
    introducerIds: introducerIds?.length ? introducerIds.join(',') : undefined,
  });

/** One page of entries; the key shares the list prefix, so demand mutations refresh it too. */
export const _getDemandEntries = (params: GetDemandEntriesParams) =>
  queryOptions({
    queryKey: [DEMAND_QUERY_KEYS.demandEntries, 'page', params],
    queryFn: () => apiDealFetch<DemandListRes>('/demand', { params: toDemandListParams(params) }),
    retry: 1,
  });

export const _getDemandEntry = (id: string) =>
  queryOptions({
    queryKey: [DEMAND_QUERY_KEYS.demandEntry, id],
    queryFn: () => apiDealFetch<DemandEntryDetail>(`/demand/${id}`),
    enabled: !!id,
  });

const joinIds = (ids?: string[]) => (ids?.length ? ids.join(',') : undefined);

/** Always Active entries only; the filters narrow which ones count. */
export const _getDemandSummary = (params: DemandSummaryParams = {}) =>
  queryOptions({
    queryKey: [DEMAND_QUERY_KEYS.demandSummary, params],
    queryFn: () =>
      apiDealFetch<DemandSummaryRes>('/demand/summary', {
        params: cleanParams({
          search: params.search,
          companyIds: joinIds(params.companyIds),
          interestedPartyIds: joinIds(params.interestedPartyIds),
          introducerIds: joinIds(params.introducerIds),
        }),
      }),
  });

export const _getDemandFilterOptions = () =>
  queryOptions({
    queryKey: [DEMAND_QUERY_KEYS.demandFilterOptions],
    queryFn: () => apiDealFetch<DemandFilterOptionsRes>('/demand/filter-options'),
  });
